import React, { useEffect, useReducer } from 'react';
import { getHouse } from '../services/ledgerService';
import { combineReducers, sortOrderMap } from '../utilities/common';
import createHouseDispatch from './houseDispatch';
import { actionTypes } from './houseActionTypes';
import { useAuth } from './AuthContext';

const DispatchContext = React.createContext(null);
const HouseContext = React.createContext(null);

const createInitialGameState = (g) => ({
    [g.id]: {
        id: g.id,
        description: g.description,
        date: g.date,
        totalBuyIns: g.totalBuyIns,
        totalCashOuts: g.totalCashOuts,
        totalNet: g.totalNet,
        numPlayers: g.numPlayers,
        pendingTransaction: {
            amount: 0,
            isSettled: true,
            paymentType: 'CASH',
            transactionType: 'BUY_IN',
        },
        players: [],
    }
});

const hidrateGameState = (g) => ({
    players: g.players.map(p => ({
        ...p,
        totals: p.transactions.reduce((acc, t) => ['BUY_IN', 'ADD_ON'].includes(t.type)
            ? {...acc, buyIn: acc.buyIn + t.amount, net: acc.net - t.amount }
            : {...acc, cashOut: acc.cashOut + t.amount, net: acc.net + t.amount },
            { buyIn: 0, cashOut: 0, net: 0 }
        ),
        pendingCoverTransactionIds: [],
    })),
});

const houseReducer = (state, { type, payload }) => {
    switch (type) {
        case actionTypes.LOAD_HOUSE:
            return { 
                ...state,  
                id: payload.id,
                description: payload.description,
                members: payload.members,
            };
        case actionTypes.ADD_MEMBER:
            return {
                ...state,
                members: [...state.members, payload.member].sort(sortByName),
            };
        case actionTypes.UPDATE_MEMBER:
            return {
                ...state,
                members: state.members.map(m =>
                    m.id !== payload.member.id 
                        ? m 
                        : {
                            ...m,
                            ...payload.member,
                        }
                ),
            };
        default:
            return state;
    }
}

const gameReducer = (state, { type, payload }) => {
    console.log("gameReducer", { type, payload });
    switch (type) {
        case actionTypes.LOAD_HOUSE:
            return  payload.games.reduce((acc, g) => ({
                ...acc,
                ...createInitialGameState(g),
            }), state)
        case actionTypes.LOAD_MULTIPLE_GAMES:
            const updatedGames = payload.games.reduce((acc, g) => ({ 
                ...acc,
                [g.id]: {
                    ...acc[g.id],
                    ...hidrateGameState(g)
                },
            }), state);
            return sortOrderMap(updatedGames, ([,a], [,b]) => a.date < b.date ? 1 : -1);
        case actionTypes.LOAD_GAME:
            return {
                ...state,
                [payload.id]: {
                    ...state[payload.id],
                    description: payload.description,
                    date: payload.date,
                    totalBuyIns: payload.totalBuyIns,
                    totalCashOuts: payload.totalCashOuts,
                    totalNet: payload.totalNet,
                    numPlayers: payload.numPlayers,
                    players: payload.players.map(p => ({
                        ...p,
                        totals: p.transactions.reduce((acc, t) => ['BUY_IN', 'ADD_ON'].includes(t.type)
                            ? {...acc, buyIn: acc.buyIn + t.amount, net: acc.net - t.amount }
                            : {...acc, cashOut: acc.cashOut + t.amount, net: acc.net + t.amount },
                            { buyIn: 0, cashOut: 0, net: 0 }
                        ),
                        pendingCoverTransactionIds: [],
                    })),
                }
            };
        case actionTypes.DELETE_GAME:
            const { [payload.gameId]: _, ...remainingGames } = state;
            return remainingGames;
        case actionTypes.ADD_GAME:
            return sortOrderMap({
                ...state, 
                ...createInitialGameState(payload),
            }, ([,a], [,b]) => a.date < b.date ? 1 : -1);
        case actionTypes.SORT_PLAYERS:
            const { sortBy, sortOrder } = payload;

            let sortFunc = sortPlayersByField(sortBy, sortOrder);

            // default sort by name
            if (sortBy === null || sortOrder === null) {
                sortFunc = sortByName;
            }

            let sortedPlayers = [...state[payload.gameId].players].sort(sortFunc);

            return {
                ...state,
                [payload.gameId]: {
                    ...state[payload.gameId],
                    players: sortedPlayers,
                },
            };
        case actionTypes.ADD_PLAYER_SUCCESS:
            const newPlayer = { 
                ...payload.player,
                totals: { buyIn: 0, cashOut: 0, net: 0 },
                pendingCoverTransactionIds: [] 
            };

            let newPlayerList = [...state[payload.gameId].players, newPlayer].sort(sortByName);

            return {
                ...state,
                [payload.gameId]: {
                    ...state[payload.gameId],
                    numPlayers: state[payload.gameId].numPlayers + 1,
                    players: newPlayerList,
                }
            };
        case actionTypes.REMOVE_PLAYER_SUCCESS:
            let remainingPlayers = state[payload.gameId].players.filter(p => p.id !== payload.playerId);
            return  {
                ...state,
                [payload.gameId]: {
                    ...state[payload.gameId],
                    numPlayers: state[payload.gameId].numPlayers - 1,
                    players: remainingPlayers,
                },
            };
        case actionTypes.UPDATE_PENDING_TRANSACTION:
            return { 
                ...state, 
                [payload.gameId]: {
                    ...state[payload.gameId],
                    pendingTransaction: { 
                        ...payload.pendingTransaction
                    } 
                }
            };
        case actionTypes.CREATE_TRANSACTION_SUCCESS:
        case actionTypes.CREATE_COVER_TRANSACTION:
            const resultingState = {
                ...state,
                [payload.gameId]: {
                    ...state[payload.gameId],
                    totalBuyIns: payload.transaction.type === "BUY_IN" ? state[payload.gameId].totalBuyIns + payload.transaction.amount : state[payload.gameId].totalBuyIns,
                    totalCashOuts: ["CASH_OUT", "COVER"].includes(payload.transaction.type) ? state[payload.gameId].totalCashOuts + payload.transaction.amount : state[payload.gameId].totalCashOuts,
                    totalNet: payload.transaction.type === "BUY_IN" ? state[payload.gameId].totalNet + payload.transaction.amount : state[payload.gameId].totalNet - payload.transaction.amount,
                    players: state[payload.gameId].players.map(p =>
                        p.id !== payload.playerId
                            ? p
                            : {
                                ...p,
                                totals: ["BUY_IN", "ADD_ON"].includes(payload.transaction.type)
                                    ? {...p.totals, buyIn: p.totals.buyIn + payload.transaction.amount, net: p.totals.net - payload.transaction.amount}
                                    : {...p.totals, cashOut: p.totals.cashOut + payload.transaction.amount, net: p.totals.net + payload.transaction.amount},
                                transactions: [...p.transactions, payload.transaction ]
                            }
                        )
                }
            };
            return resultingState;
        case actionTypes.DELETE_TRANSACTION_SUCCESS:
            return {
                ...state,
                [payload.gameId]: {
                    ...state[payload.gameId],
                    totalBuyIns: payload.transaction.type === "BUY_IN" ? state[payload.gameId].totalBuyIns - payload.transaction.amount : state[payload.gameId].totalBuyIns,
                    totalCashOuts: ["CASH_OUT", "COVER"].includes(payload.transaction.type) ? state[payload.gameId].totalCashOuts - payload.transaction.amount : state[payload.gameId].totalCashOuts,
                    totalNet: payload.transaction.type === "BUY_IN" ? state[payload.gameId].totalNet - payload.transaction.amount : state[payload.gameId].totalNet + payload.transaction.amount,
                    players: state[payload.gameId].players.map(p =>
                        p.id !== payload.playerId
                            ? p
                            : {
                                ...p,
                                totals: ["BUY_IN", "ADD_ON"].includes(payload.transaction.type)
                                    ? {...p.totals, buyIn: p.totals.buyIn - payload.transaction.amount, net: p.totals.net + payload.transaction.amount}
                                    : {...p.totals, cashOut: p.totals.cashOut - payload.transaction.amount, net: p.totals.net - payload.transaction.amount},
                                transactions: p.transactions.filter(t => t.id !== payload.transaction.id)
                            }
                    )
                }
            };
        case actionTypes.TOGGLE_TRANSACTION_STATUS:
        case actionTypes.CHANGE_TRANSACTION_PAYMENT_TYPE:
            return {
                ...state,
                [payload.gameId]: {
                    ...state[payload.gameId],
                    players: state[payload.gameId].players.map(p =>
                        p.id !== payload.playerId
                            ? p
                            : {
                                ...p,
                                transactions: p.transactions.map(t =>
                                    t.id !== payload.transaction.id
                                        ? t
                                        :   { ...payload.transaction }
                                )
                            }
                    )
                }
            };
        case actionTypes.ADD_COVER_CANDIDATE:
            return {
                ...state,
                [payload.gameId]: {
                    ...state[payload.gameId],
                    pendingTransaction: {
                        ...state[payload.gameId].pendingTransaction,
                        amount: state[payload.gameId].pendingTransaction.amount - payload.transaction.amount,
                    },
                    players: state[payload.gameId].players.map(p =>
                        p.id !== payload.playerId ? p : {
                            ...p,
                            transactions: p.transactions.map(t =>
                                t.id !== payload.transaction.id ? t : {
                                    ...t,
                                    isSettled: true,
                                }
                            ),
                            pendingCoverTransactionIds: [...p.pendingCoverTransactionIds, payload.transaction.id]
                        }
                    )
                }
            };
        case actionTypes.REMOVE_COVER_CANDIDATE:
            return {
                ...state,
                [payload.gameId]: {
                    ...state[payload.gameId],
                    pendingTransaction: {
                        ...state[payload.gameId].pendingTransaction,
                        amount: state[payload.gameId].pendingTransaction.amount + payload.transaction.amount,
                    },
                    players: state[payload.gameId].players.map(p =>
                        p.id !== payload.playerId ? p : {
                            ...p,
                            transactions: p.transactions.map(t =>
                                t.id !== payload.transaction.id ? t : {
                                    ...t,
                                    isSettled: false,
                                }
                            ),
                            pendingCoverTransactionIds: p.pendingCoverTransactionIds.filter(id => id !== payload.transaction.id)
                        }
                    )
                }
            };
        case actionTypes.RESET_COVER_CANDIDATE:
            return {
                ...state,
                [payload.gameId]: {
                    ...state[payload.gameId],
                    players: state[payload.gameId].players.map(p =>
                        p.id !== payload.playerId ? p : {
                            ...p,
                            transactions: p.transactions.map(t =>
                                !p.pendingCoverTransactionIds.includes(t.id) ? t : {
                                    ...t,
                                    isSettled: false,
                                }
                            ),
                            pendingCoverTransactionIds: [],
                        }
                    )
                }
            };
        case actionTypes.CLEAR_COVER_CANDIDATES:
            return {
                ...state,
                [payload.gameId]: {
                    ...state[payload.gameId],
                    players: state[payload.gameId].players.map(p =>
                        p.id !== payload.playerId ? p : {
                            ...p,
                            pendingCoverTransactionIds: [],
                        }
                    )
                }
            };
        default:
            return state;
    }
};

const reducer = combineReducers({
    house: houseReducer,
    games: gameReducer,
});

export default function HouseProvider({ children }) {
    const [state, dispatch] = useReducer(reducer, { house: null, games: {} });
    const { isAuthenticated } = useAuth();

    useEffect(() => {
        const fetchHouse = async () => {
            try {
                const houseResponse = await getHouse();
                dispatch({ type: 'LOAD_HOUSE', payload: houseResponse });
            } catch (e) {
                console.error('Failed to fetch house:', e);
            }
        };
        if (isAuthenticated) {
            fetchHouse();
        }
    }, [isAuthenticated]);

    if (!state.house || !isAuthenticated) {
        return <div>Loading House...</div>;
    }

    const houseDispatch = createHouseDispatch(state, dispatch);

    return (
        <DispatchContext.Provider value={ houseDispatch }>
            <HouseContext.Provider value={ state }>
                {children}
            </HouseContext.Provider>
        </DispatchContext.Provider>
    );
}

export const useHouseDispatch = () => {
    const context = React.useContext(DispatchContext);
    if (!context) {
        throw new Error('useHouseDispatch must be used within a HouseProvider');
    }
    return context;
};

export const useHouseState = () => {
    const context = React.useContext(HouseContext);
    if (!context) {
        throw new Error('useHouseState must be used within a HouseProvider');
    }
    return context;
};

const sortPlayersByField = (sortBy, sortOrder) => 
    (a, b) => {
        const comparison = a.totals[sortBy] > b.totals[sortBy] ? 1 : -1;
        return sortOrder === 'asc' ? comparison : -comparison;
    };

const sortByName = (a, b) => a.name.localeCompare(b.name);
